import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API from "../../network/ApiCall";

import {
  AUTHENTICATION_TOKEN_ENDPOINT,
  AUTH_TOKEN_NAME,
  OAUTH_CLIENT_ID,
  OAUTH_CLIENT_SECRET,
  API_URL,
  ADMIN_APP_URL,
} from "../../config";
import axiosInstance from "../../network/ApiCall";

const ACCOUNT_DETAILS_ENDPOINT = "/account/me";

const createAsyncThunk = require("@reduxjs/toolkit").createAsyncThunk;

const userToken = localStorage.getItem(AUTH_TOKEN_NAME)
  ? JSON.parse(localStorage.getItem(AUTH_TOKEN_NAME))
  : null;

const initialState = {
  loading: false,
  loadingUser: false,
  error: "",
  isLoggedIn: userToken ? true : false,
  userToken,
  user: null
};

export const getAccount = createAsyncThunk(
  "auth/getAccount",
  async (code, { rejectWithValue }) => {    
    const url = ACCOUNT_DETAILS_ENDPOINT;

    try {
      const response = await API.get(url);

      return response.data;
    } catch (error) {
  
      return rejectWithValue('API error');
    }
  }
);


export const exchangeCodeForToken = createAsyncThunk(
  "auth/exchangeCodeForToken",
  async (code, { rejectWithValue }) => {
  
    const url = AUTHENTICATION_TOKEN_ENDPOINT;
    const params = {
      code: code,
      redirect_uri: ADMIN_APP_URL + '/auth/OauthCallback',
      grant_type: "authorization_code",
    };

    try {

      const response = await axios.post(url, params, {
        auth: {
          username: OAUTH_CLIENT_ID,
          password: OAUTH_CLIENT_SECRET
        },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        }
      })
      // Store authentication token in local storage
      localStorage.setItem(
        AUTH_TOKEN_NAME,
        JSON.stringify(response.data)
      );      
      return response.data;
    } catch (error) {

      console.error("ER  ", error);
      return rejectWithValue('axios error');
    }
  }
);

export const exchangeCodeForTokenIdp = createAsyncThunk(
  "auth/exchangeCodeForTokenIdp",
  async (payload, { rejectWithValue }) => {
    const { code, countryCode, redirectUri } = payload || {};
    if (!code || !countryCode || !redirectUri) {
      return rejectWithValue("Missing required parameters for IDP token exchange");
    }

    try {
      const response = await axios.post(`${API_URL}/oauth2/exchange`, {
        code,
        countryCode,
        redirectUri
      });
      localStorage.setItem(AUTH_TOKEN_NAME, JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error("IDP token exchange error", error);
      return rejectWithValue("idp axios error");
    }
  }
);

export const exchangeSamlArtifact = createAsyncThunk(
  "auth/exchangeSamlArtifact",
  async (code, { rejectWithValue }) => {
    if (!code) {
      console.log("[SAML] exchangeSamlArtifact invoked without an artifact code");
      return rejectWithValue("Missing SAML artifact");
    }

    try {
      const response = await axios.post(`${API_URL}/saml/exchange`, { code });

      localStorage.setItem(AUTH_TOKEN_NAME, JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      const responseStatus = error?.response?.status;
      const responseData = error?.response?.data;
      console.error("[SAML] SAML artifact exchange failed", {
        status: responseStatus,
        data: responseData,
        message: error?.message
      });
      return rejectWithValue("saml axios error");
    }
  }
);

// TODO: Add unit tests to assert OAuth and SAML exchanges set authState correctly once test tooling is available.

/**
 * Dynamic OIDC code exchange for union-mode.
 * Expects: { tokenEndpoint, code, codeVerifier, clientId, redirectUri }
 * Uses PKCE (no client secret).
 */
export const exchangeCodeForTokenDynamic = createAsyncThunk(
  "auth/exchangeCodeForTokenDynamic",
  async (payload, { rejectWithValue }) => {
    const { tokenEndpoint, code, codeVerifier, clientId, redirectUri } = payload || {};
    if (!tokenEndpoint || !code || !codeVerifier || !clientId || !redirectUri) {
      return rejectWithValue("Missing required parameters for dynamic token exchange");
    }

    // Form-URL-encoded body as per RFC 6749
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code: code,
      client_id: clientId,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier
    }).toString();

    try {
      const response = await axios.post(tokenEndpoint, body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
      });

      // Persist token payload just like the legacy flow
      localStorage.setItem(AUTH_TOKEN_NAME, JSON.stringify(response.data));

      return response.data;
    } catch (error) {
      console.error("Dynamic token exchange error", error);
      return rejectWithValue("dynamic axios error");
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async () => {
    localStorage.removeItem(AUTH_TOKEN_NAME);      
  }
)

export const authSlice = createSlice({
  initialState,
  name: "authSlice",
  reducers: {      
    updateAccountState: (state, action) => {  

      return {...state, user: {...action.payload}};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(exchangeCodeForToken.pending, (state) => {

        state.loading = true;
        state.isLoggedIn = false;
      })
      .addCase(exchangeCodeForToken.fulfilled, (state, { payload }) => {

        state.userToken = payload;
        state.loading = false;
        state.isLoggedIn = true;

        // logged in, record login activity (though we may not have an account yet)
        axiosInstance.get('/recordLogin')
      })
      .addCase(exchangeCodeForToken.rejected, (state) => {
        console.log("EXCHANGE rejected", state)

        state.loading = false;
        state.isLoggedIn = false;
        state.error = "API_ERROR"
      })

      .addCase(exchangeCodeForTokenIdp.pending, (state) => {
        state.loading = true;
        state.isLoggedIn = false;
      })
      .addCase(exchangeCodeForTokenIdp.fulfilled, (state, { payload }) => {
        state.userToken = payload;
        state.loading = false;
        state.isLoggedIn = true;

        axiosInstance.get('/recordLogin')
      })
      .addCase(exchangeCodeForTokenIdp.rejected, (state) => {
        state.loading = false;
        state.isLoggedIn = false;
        state.error = "API_ERROR"
      })

      .addCase(exchangeSamlArtifact.pending, (state) => {

        state.loading = true;
        state.isLoggedIn = false;
        state.error = "";
      })
      .addCase(exchangeSamlArtifact.fulfilled, (state, { payload }) => {
        state.userToken = payload;
        state.loading = false;
        state.isLoggedIn = true;
        axiosInstance.get('/recordLogin');
      })
      .addCase(exchangeSamlArtifact.rejected, (state, { payload }) => {
        console.log("[SAML] exchangeSamlArtifact rejected", payload);
        state.loading = false;
        state.isLoggedIn = false;
        state.error = payload || "API_ERROR";
      })

      .addCase(exchangeCodeForTokenDynamic.pending, (state) => {
        state.loading = true;
        state.isLoggedIn = false;
        state.error = "";
      })
      .addCase(exchangeCodeForTokenDynamic.fulfilled, (state, { payload }) => {
        state.userToken = payload;
        state.loading = false;
        state.isLoggedIn = true;
        // Keep parity with legacy: record login
        axiosInstance.get('/recordLogin');
      })
      .addCase(exchangeCodeForTokenDynamic.rejected, (state, { payload }) => {
        console.log("DYNAMIC EXCHANGE rejected", state, payload)
        state.loading = false;
        state.isLoggedIn = false;
        state.error = payload || "API_ERROR";
      })
      
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.isLoggedIn = false;
        state.userToken= null;
      })

      .addCase(getAccount.pending, (state) => {
        state.loading = false;
        state.user = null;
        state.loadingUser = true;
        state.error = null;
      })
      .addCase(getAccount.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.loadingUser = false;
        state.error = null;
        if (payload.roles.includes("ADMIN")) {          
          state.user = payload;
        } else {
          state.loading = false;
          state.loadingUser = false;
          state.isLoggedIn = false;
          state.error = "ACCESSDENIED"
          state.user = null;
        }
      })
      .addCase(getAccount.rejected, (state) => {
        state.loading = false;
        state.loadingUser = false;
        state.isLoggedIn = false;
        state.error = "API_ERROR"
        state.user = null;
      });
  },
});

export const {updateAccountState} = authSlice.actions;

export default authSlice.reducer;
